import { toaster } from '@/components/ui/toaster';
import { usePlanFeaturesQuery } from '@/hooks/queries/use-plan-features-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import shopifyLogo from '@/assets/icons/platforms/shopify.svg';
import { FeatureFlag } from '@fluenceur/types';
import IntegrationCard from './IntegrationCard';
import type { IntegrationCardVariant } from './IntegrationCard';
import ShopifyStoreUrlModal from './ShopifyStoreUrlModal';
import ShopifyUninstallGuideDialog from './ShopifyUninstallGuideDialog';

interface ShopifyIntegrationProps {
  id: string | null;
  isConnected: boolean;
  metadata?: {
    shopDomain?: string;
  };
  variant?: IntegrationCardVariant;
  detailHref?: string;
}

export default function ShopifyIntegration(props: ShopifyIntegrationProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { hasFeature } = usePlanFeaturesQuery();
  const { id, isConnected, metadata, variant, detailHref } = props;
  const isShopifyEnabled = hasFeature(FeatureFlag.SHOPIFY_INTEGRATION);
  const [showStoreUrlModal, setShowStoreUrlModal] = useState(false);
  const [showUninstallGuideDialog, setShowUninstallGuideDialog] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('state');
    const reauth = params.get('reauth');

    if (state === 'shopify_callback') {
      toaster.create({
        description: t('integrations.shopify.success.connected'),
        type: 'success',
      });
    }

    if (reauth === 'success') {
      toaster.create({
        description: t('integrations.shopify.scopeReauth.success'),
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (reauth === 'incomplete') {
      const scopes = params
        .get('missingScopes')
        ?.split(',')
        .map((scope) => scope.trim())
        .filter(Boolean)
        .join(', ');

      toaster.create({
        description: t('integrations.shopify.scopeReauth.incomplete', { scopes }),
        type: 'warning',
      });
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [t, queryClient]);

  function handleToggle(): void {
    if (isConnected && id) {
      setShowUninstallGuideDialog(true);
    } else {
      setShowStoreUrlModal(true);
    }
  }

  return (
    <>
      <IntegrationCard
        name={t('integrations.shopify.name')}
        description={t('integrations.shopify.description')}
        icon={shopifyLogo}
        iconStyle="mark"
        isActive={isConnected}
        isLoading={false}
        disabled={!isShopifyEnabled}
        onToggle={handleToggle}
        showConfirmDialog={false}
        connectedAs={metadata?.shopDomain}
        connectedLabel={t('integrations.connectedLabel')}
        variant={variant}
        detailHref={detailHref}
      />
      <ShopifyStoreUrlModal
        isOpen={showStoreUrlModal}
        onClose={() => setShowStoreUrlModal(false)}
      />
      <ShopifyUninstallGuideDialog
        isOpen={showUninstallGuideDialog}
        storeName={t('integrations.shopify.name')}
        shopDomain={metadata?.shopDomain}
        onClose={() => setShowUninstallGuideDialog(false)}
      />
    </>
  );
}
